import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Observable } from 'rxjs';

import {
  LanResponse,
  AgentResponse,
  PresignedUrlResponse,
  PriceDetails,
  PriceInput,
} from '../interfaces/upload-video.interface';
import {
  ApiResponse,
  ApiResponseModel,
} from '@app/core/models/api-response.model';
import {
  MovieModel,
  SearchModel,
} from '@app/pages/shared/interfaces/movie.model';
import { FilmContestModel } from '@app/pages/shared/interfaces/film-contest.model';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UploadFilmService {
  baseUrl: string = environment.apiUrl;

  constructor(public http: HttpClient) { }

  uploadImages({
    filmInfo,
    type,
    isPriceUpdate,
  }: {
    filmInfo: any;
    type?: string;
    isPriceUpdate?: boolean;
  }): Observable<any> {
    let url = `${this.baseUrl}/film-maker/film`;
    const params: string[] = [];
    if (type) params.push(`type=${type}`);
    if (isPriceUpdate) params.push(`isPriceUpdate=${isPriceUpdate}`);
    if (params.length) url += `?${params.join('&')}`;
    return this.http.post<any>(url, filmInfo);
  }

  getAgent(): Observable<AgentResponse> {
    return this.http.get<AgentResponse>(`${this.baseUrl}/agent`);
  }

  getLanguage(): Observable<LanResponse> {
    return this.http.get<LanResponse>(`${this.baseUrl}/language`);
  }

  getSubTitle(file: any, type: string): Observable<LanResponse> {
    return this.http.post<LanResponse>(
      `${this.baseUrl}/film-maker/film/upload-subtitle`,
      file
    );
  }

  getAge(): Observable<LanResponse> {
    return this.http.get<LanResponse>(`${this.baseUrl}/film/age-group`);
  }

  uploadVideoToS3(url: string, film: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': film.type,
      'skip-Auth': 'true',
    });
    return this.http
      .put<any>(url, film, {
        reportProgress: true,
        observe: 'events',
        headers,
      })
      .pipe(map((event: HttpEvent<any>) => this.getEventProgress(event)));
  }

  getPresignedUrl(
    type: string,
    extention: string,
    contentType: string
  ): Observable<PresignedUrlResponse> {
    let params = new HttpParams()
      .set('type', type)
      .set('extension', extention)
      .set('contentType', contentType);

    return this.http.get<any>(
      `${this.baseUrl}/film-maker/film/generatePresignedUrl`,
      { params }
    );
  }

  private getEventProgress(event: HttpEvent<any>): number {
    switch (event.type) {
      case HttpEventType.UploadProgress:
        // return event.total ? Math.round(100 * event.loaded / event.total) : 0;
        return Math.round((100 * event.loaded) / (event.total ?? 1));
      case HttpEventType.Response:
        return 100; // Upload complete
      default:
        return 0;
    }
  }

  uploadVideo(film: any, videoType: string): Observable<any> {
    const headers = new HttpHeaders({ 'ngsw-bypass': '' });
    return this.http.post<any>(
      `${this.baseUrl}/film-maker/film/upload?type=${videoType}`,
      film,
      {
        reportProgress: true,
        observe: 'events',
        headers,
      }
    );
  }

  getFilmList(
    page: number,
    limit: number,
    sortOrder: 'desc' | 'asc',
    key: string,
    matchParams: string
  ): Observable<ApiResponse<MovieModel[]>> {
    return this.http.get<ApiResponse<MovieModel[]>>(
      `${this.baseUrl}/film-maker/filmList?page=${page}&limit=${limit}&sortOrder=${sortOrder}&key=${key}&matchParams=${matchParams}`
    );
  }

  getOpratingCostByTime(
    duration: number,
    filmContest: boolean
  ): Observable<LanResponse> {
    return this.http.get<LanResponse>(
      `${this.baseUrl}/ticket-price?duration=${duration}&isContest=${filmContest}`
    );
  }

  getFilmById(filmId: string | null): Observable<ApiResponseModel<MovieModel>> {
    return this.http.get<ApiResponseModel<MovieModel>>(
      `${this.baseUrl}/film-maker/film?id=${filmId}`
    );
  }

  saveAsDraft(filmData: any): Observable<ApiResponseModel<MovieModel>> {
    return this.http.post<ApiResponseModel<MovieModel>>(
      `${this.baseUrl}/film-maker/save-as-draft`,
      filmData
    );
  }

  searchFilm(
    key: string,
    page: number,
    limit: number,
    isSearchPage: boolean,
    filmSkipNumber?: number,
    webSeriesSkipNumber?: number,
    microSeriesSkipNumber?: number,
    filmHasMore?: boolean,
    webSeriesHasMore?: boolean,
    microSeriesHasMore?: boolean,
  ): Observable<ApiResponseModel<SearchModel>> {
    let queryParams = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('isSearchPage', isSearchPage);

    if (filmSkipNumber !== undefined)
      queryParams = queryParams.set('filmSkipNumber', filmSkipNumber);

    if (webSeriesSkipNumber !== undefined)
      queryParams = queryParams.set('webSeriesSkipNumber', webSeriesSkipNumber);

    if (microSeriesSkipNumber !== undefined)
      queryParams = queryParams.set('microSeriesSkipNumber', microSeriesSkipNumber);

    if (filmHasMore !== undefined)
      queryParams = queryParams.set('filmHasMore', filmHasMore);

    if (webSeriesHasMore !== undefined)
      queryParams = queryParams.set('webSeriesHasMore', webSeriesHasMore);

    if (microSeriesHasMore !== undefined)
      queryParams = queryParams.set('microSeriesHasMore', microSeriesHasMore);

    console.log(queryParams.toString());
    return this.http.get<ApiResponseModel<SearchModel>>(
      `${this.baseUrl}/viewer/content/search/${encodeURIComponent(
        key
      )}?${queryParams.toString()}`
    );
  }

  filmContestDetail(): Observable<ApiResponseModel<FilmContestModel>> {
    return this.http.get<ApiResponseModel<FilmContestModel>>(
      `${this.baseUrl}/film/contest/detail`
    );
  }

  filmContestLanguage(): Observable<LanResponse> {
    return this.http.get<LanResponse>(`${this.baseUrl}/film/contest/languages`);
  }

  getPricingDetail(param: PriceInput): Observable<ApiResponse<PriceDetails>> {
    return this.http.post<ApiResponse<PriceDetails>>(
      `${this.baseUrl}/film-maker/calculate/filmPrice`,
      param
    );
  }

  getFilteredTransaction(
    page: number,
    limit: number,
    sortOrder: 'desc' | 'asc',
    key: string,
    matchParams: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${this.baseUrl}/wallet/filmMaker/transactions?page=${page}&limit=${limit}&sortOrder=${sortOrder}&key=${key}&matchParams=${matchParams}`
    );
  }
}
